import { useEffect, useRef, useState } from "react";
import { importantCards } from "@/mocks/news";

const CARD_COUNT = importantCards.length;
// Each card gets 100vh of scroll distance
const SCROLL_PER_CARD = 1; // multiplier of vh

const ImportantSection = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  // cardProgress: 0~1 within each card's scroll zone (for enter animation)
  const [cardProgress, setCardProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const section = sectionRef.current;
      if (!section) return;

      const rect = section.getBoundingClientRect();
      // scrolledIn: how many px we've scrolled past the section top
      const scrolledIn = -rect.top;
      const vh = window.innerHeight;
      // Each card occupies one vh of scroll
      const perCard = vh * SCROLL_PER_CARD;

      if (scrolledIn < 0) {
        setActiveIndex(0);
        setCardProgress(0);
        return;
      }

      const rawIndex = scrolledIn / perCard;
      const idx = Math.min(Math.floor(rawIndex), CARD_COUNT - 1);
      const progress = rawIndex - Math.floor(rawIndex);

      setActiveIndex(idx);
      setCardProgress(progress);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Section height: enough scroll room for all cards + exit
  // Extra 1vh added so the last card fully animates in before exit
  const sectionHeight = `${(CARD_COUNT + 1) * 100}vh`;

  return (
    <section
      id="important"
      ref={sectionRef}
      className="relative bg-[#f5f0e8]"
      style={{ height: sectionHeight }}
    >
      {/* Sticky viewport — offset by header height (80px) */}
      <div
        className="sticky top-0 bg-[#f5f0e8] overflow-hidden"
        style={{ height: "100vh", paddingTop: "72px" }}
      >
        <div className="h-full flex items-center px-6">
          <div className="max-w-6xl mx-auto w-full">
            <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-8">

              {/* ── Left: always visible title + pills ── */}
              <div className="lg:w-80 shrink-0">
                <p className="text-green-700 text-xs font-semibold tracking-widest mb-2">
                  「よりどころ」であるために
                </p>
                <h2 className="text-gray-800 text-2xl lg:text-3xl font-bold leading-snug">
                  みどり苑の
                  <br />
                  大切にしていること
                </h2>

                {/* Pill indicators — reflect scroll position */}
                <div className="flex gap-2 mt-5 flex-wrap">
                  {importantCards.map((c, i) => (
                    <div
                      key={c.number}
                      className={`flex items-center gap-1.5 px-4 py-2 rounded-full border text-xs font-semibold transition-all duration-300 ${
                        i === activeIndex
                          ? "bg-green-700 border-green-700 text-white"
                          : i < activeIndex
                          ? "bg-green-100 border-green-200 text-green-700"
                          : "bg-transparent border-gray-300 text-gray-400"
                      }`}
                    >
                      <span>{c.number}</span>
                      <span>{c.label}</span>
                    </div>
                  ))}
                </div>

                {/* Scroll hint */}
                <p className="mt-6 text-gray-400 text-xs flex items-center gap-2">
                  <i className="ri-mouse-line text-base" />
                  スクロールで切り替わります
                </p>
              </div>

              {/* ── Right: card stack — only active card visible ── */}
              <div className="flex-1 max-w-xl relative" style={{ minHeight: 340 }}>
                {importantCards.map((c, i) => {
                  const isActive = i === activeIndex;
                  const isPast = i < activeIndex;

                  // Enter: slide up from below when becoming active
                  // Exit: slide up and fade out when becoming past
                  let transform = "translateY(60px)";
                  let opacity = 0;
                  let zIndex = 0;

                  if (isActive) {
                    // Ease in as cardProgress goes 0→1 (but snap in quickly)
                    const ease = Math.min(cardProgress * 4, 1);
                    const y = 60 * (1 - ease);
                    transform = `translateY(${y}px)`;
                    opacity = ease;
                    zIndex = 10;
                  } else if (isPast) {
                    transform = "translateY(-20px)";
                    opacity = 0;
                    zIndex = 5;
                  }

                  return (
                    <div
                      key={c.number}
                      className="absolute inset-0 transition-none"
                      style={{ transform, opacity, zIndex }}
                    >
                      <div className="bg-white rounded-3xl p-6 lg:p-8">
                        <div className="flex justify-between items-center text-green-700 text-xs font-semibold mb-3">
                          <span>{c.number}</span>
                          <span>{c.label}</span>
                        </div>

                        <div className="w-28 lg:w-36 mx-auto aspect-square overflow-hidden rounded-2xl">
                          <img
                            src={c.image1}
                            alt={c.title}
                            className="w-full h-full object-cover"
                          />
                        </div>

                        <div className="mt-4 text-center">
                          <h3 className="text-3xl lg:text-4xl font-bold text-gray-800">
                            {c.title}
                          </h3>
                        </div>

                        <div className="mt-4 border-t border-dashed border-gray-200 pt-4">
                          <ul className="flex flex-col gap-2">
                            {c.points.map((point) => (
                              <li
                                key={point}
                                className="flex items-start gap-2 text-gray-600 text-sm leading-relaxed"
                              >
                                <span className="w-1.5 h-1.5 rounded-full bg-green-500 mt-2 shrink-0" />
                                {point}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ImportantSection;
